import type { Customer } from '@/lib/services/customers/customers.types'
import type { AdminRouterContext } from '@/lib/router-context'
import { RecordHistoryPage } from '@/components/audit-log/RecordHistoryPage'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { auditLogQueries } from '@/lib/services/audit-log/audit-log.query'
import { customerQueries } from '@/lib/services/customers/customers.query'
import { customerIdSchema } from '@/lib/services/customers/customers.schema'
import { createFileRoute, notFound } from '@tanstack/react-router'

export const Route = createFileRoute('/_authenticated/$orgId/customers/$customerId_/history')({
  component: CustomerHistoryPage,
  params: {
    parse: (params) => customerIdSchema.parse(params),
  },
  head: ({ loaderData, params }) =>
    adminTitleHead(
      'History',
      loaderData?.customer?.name || `Customer #${params?.customerId}`,
      'Customers'
    ),
  loader: loadCustomerHistory,
})

export async function loadCustomerHistory({
  context,
  params,
}: {
  context: AdminRouterContext
  params: { orgId: number | string; customerId: number | string }
}): Promise<{ customer: Customer }> {
  if (!context.ability.can('view', 'audit-logs') || !context.ability.can('view', 'customers')) {
    throw notFound()
  }

  const [customer] = await Promise.all([
    context.queryClient.ensureQueryData(
      customerQueries.show({
        organizationId: params.orgId,
        customerId: params.customerId,
      })
    ),
    context.queryClient.ensureInfiniteQueryData(
      auditLogQueries.record({
        organizationId: params.orgId,
        subject: 'customers',
        recordId: params.customerId,
      })
    ),
  ])

  return { customer }
}

export function CustomerHistoryPage() {
  const { customerId, orgId } = Route.useParams()
  const { customer } = Route.useLoaderData()
  const path = usePathHelper()

  return (
    <RecordHistoryPage
      backLabel="customer"
      backLink={path.customers.show(customerId).link}
      description="Review important changes to this customer's profile, contacts, billing, and payment methods."
      organizationId={orgId}
      recordId={customerId}
      recordLabel={customer.name || `Customer #${customerId}`}
      subject="customers"
    />
  )
}
